import { BackButton } from '@/components/shared/back-button';
import { cn } from '@/lib/utils';
import { CheckCircle, XCircle, HelpCircle, MessageSquare } from 'lucide-react';
import type { QuizDetail, QuizAttempt } from '@/types';

export function QuizResults({
  quiz,
  attempt,
}: {
  quiz: QuizDetail;
  attempt: { score: number; passed: boolean; answers: QuizAttempt['answers'] };
}) {
  const { passed, score, answers } = attempt;
  const showDetails = quiz.show_correct_answers && answers.length > 0;
  const questions = [...quiz.questions].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="mx-auto max-w-2xl space-y-6 py-12">
      {/* Score card */}
      <div className="rounded-xl border border-border bg-card p-8 text-center">
        {passed ? (
          <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
        ) : (
          <XCircle className="mx-auto h-16 w-16 text-red-500" />
        )}
        <h2 className={cn('mt-4 text-2xl font-bold', passed ? 'text-green-600' : 'text-red-600')}>
          {passed ? 'Aprobado' : 'No Aprobado'}
        </h2>
        <p className="mt-2 text-lg text-muted-foreground">
          Tu puntaje: <span className="font-bold text-foreground">{Math.round(score)}%</span>
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Puntaje mínimo: {quiz.passing_percent}%
        </p>
      </div>

      {/* Per-question feedback */}
      {showDetails && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-foreground">Detalle por pregunta</h3>
          {questions.map((q, idx) => {
            const answer = answers.find((a) => a.question_id === q.id);
            const isCorrect = answer?.is_correct;
            const isWritten = q.question_type === 'written';

            return (
              <div
                key={q.id}
                className={cn(
                  'rounded-xl border p-4',
                  isWritten
                    ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30'
                    : isCorrect
                      ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30'
                      : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30',
                )}
              >
                {/* Question header */}
                <div className="flex items-start gap-2">
                  {isWritten ? (
                    <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                  ) : isCorrect ? (
                    <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
                  ) : (
                    <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-card-foreground">
                      {idx + 1}. {q.content}
                    </p>

                    {/* Choices feedback */}
                    {q.question_type !== 'written' && (
                      <div className="mt-2 space-y-1">
                        {[...q.choices]
                          .sort((a, b) => a.sort_order - b.sort_order)
                          .map((choice) => {
                            const wasSelected = answer?.choice_id === choice.id;
                            return (
                              <div
                                key={choice.id}
                                className={cn(
                                  'flex items-center gap-2 rounded-md px-2.5 py-1 text-xs',
                                  choice.is_correct &&
                                    'font-medium text-green-700 dark:text-green-400',
                                  wasSelected &&
                                    !choice.is_correct &&
                                    'font-medium text-red-700 dark:text-red-400',
                                  !wasSelected && !choice.is_correct && 'text-muted-foreground',
                                )}
                              >
                                {choice.is_correct && (
                                  <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                                )}
                                {wasSelected && !choice.is_correct && (
                                  <XCircle className="h-3.5 w-3.5 text-red-500" />
                                )}
                                {!wasSelected && !choice.is_correct && (
                                  <span className="h-3.5 w-3.5" />
                                )}
                                <span>{choice.content}</span>
                                {wasSelected && (
                                  <span className="text-muted-foreground">(tu respuesta)</span>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}

                    {/* Written answer */}
                    {isWritten && answer?.written_answer && (
                      <p className="mt-2 text-xs italic text-muted-foreground">
                        Tu respuesta: &quot;{answer.written_answer}&quot;
                        <span className="ml-1 not-italic text-amber-600">
                          (pendiente de revisión)
                        </span>
                      </p>
                    )}

                    {/* Explanation */}
                    {q.explanation && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
                        <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>{q.explanation}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="text-center">
        <BackButton label="Volver al Curso" />
      </div>
    </div>
  );
}
